import React, { useEffect, useState, useRef } from "react";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "../../../Utils/FormInputs/TextField";
import ReusableList from "./ReusableList";
import { NoTransaction } from "./ReusableJournalTable";
import { titleCase } from "../../../Utils/titleCaseFunction";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import * as snackbarActions from "../../../Redux/Actions/SnackbarActions/SnackbarActions";
import PharmacySnackbar from "../../../Utils/Snackbars/SnackbarUtil";
import ReusableDashboardHeader from "../../../Containers/Dasboard/ReusableDashboardHeader";
import { LoadingGif } from "../../../Utils/Loader";
import LedgerEntry from "./LedgerEntry";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { errorMessages } from "../../../Helpers/ErrorMessages";
import JournalList from "./JournalList";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    width: "100%",
    height: "calc(100vh - 150px)", // adjust based on header height
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      height: "auto",
    },
  },
  leftPanel: {
    height: "100%",
    overflowY: "auto",
    backgroundColor: "#f9f9f9",
    borderRight: "1px solid #c7c6c7",
    minWidth: 200,
    maxWidth: "80%",
    padding: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#c7c6c7",
      borderRadius: "10px",
    },
  },
  rightPanel: {
    flex: 1,
    height: "100%",
    overflowY: "auto",
    padding: theme.spacing(1),
  },
  divider: {
    width: 5,
    cursor: "col-resize",
    backgroundColor: "#e0e0e0",
    "&:hover": {
      backgroundColor: "#c7c6c7",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: 5,
      cursor: "row-resize",
    },
  },
  searchFieldWrapper: {
    marginBottom: theme.spacing(1),
  },
  body1: {
    fontSize: "1em",
  },
  body2: {
    color: "rgba(0, 0, 0, 0.54)",
    fontSize: "0.85em",
  },
}));

const Journals = ({ actions, snackbars }) => {
  const classes = useStyles();

  const [allJournals, setAllJournals] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingJournals, setLoadingJournals] = useState("idle");
  const [specificItem, setItem] = useState({ id: "", head_name: "", head_code: "" });
  const [isSubmitLedger, setIsSubmitLedger] = useState("idle");

  const [leftWidth, setLeftWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  const containerRef = useRef();

  const { head_name, head_code } = specificItem;
  const isResolved = isSubmitLedger === "resolved";

  useEffect(() => {
    setLoadingJournals("loading");
    axios
      .get(`${config.epharmUrl}/accounting/chart/journals`)
      .then((res) => {
        const data = res.data?.data || [];
        const dat = data.map((item, index) => ({
          ...item,
          isActive: index === 0 ? "active" : "inactive",
        }));
        setAllJournals(dat);
        setItem(data[0] || {});
        setLoadingJournals("success");
      })
      .catch((err) => {
        errorMessages(err, null, actions);
        setLoadingJournals("error");
      });
  }, [isResolved, actions]);

  const handleClickJournal = (item) => {
    const updated = allJournals.map((i) =>
      i.id === item.id ? { ...i, isActive: "active" } : { ...i, isActive: "inactive" }
    );
    setAllJournals(updated);
    setItem(item);
  };

  const handleSearch = (e) => setSearch(e.target.value);

  const filteredList = (arr) =>
    arr.filter((d) =>
      search === "" ? d : titleCase(d.head_name).includes(titleCase(search))
    );

  const closeSnackbar = () => actions.snackbarActions.hideSnackBar();

  // =======================
  // Resizing logic
  // =======================
  const startResize = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const stopResize = () => {
    setIsResizing(false);
  };

  const handleResize = (e) => {
    if (!isResizing || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    if (window.innerWidth > 600) {
      // horizontal resize
      const newWidth = e.clientX - containerRect.left;
      if (newWidth >= 200 && newWidth <= containerRect.width * 0.8) setLeftWidth(newWidth);
    } else {
      // vertical resize for mobile
      const newHeight = e.clientY - containerRect.top;
      if (newHeight >= 100) setLeftWidth(newHeight);
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleResize);
    window.addEventListener("mouseup", stopResize);
    return () => {
      window.removeEventListener("mousemove", handleResize);
      window.removeEventListener("mouseup", stopResize);
    };
  });

  const { open: openBar, variant, message } = snackbars;

  return (
    <div className="journals">
      <ReusableDashboardHeader
        component="Journals"
        heading="Accounting"
        subHeading="Journals"
        link="/accounting/journals"
      />

      <PharmacySnackbar
        message={message}
        open={openBar}
        handleCloseBar={closeSnackbar}
        variant={variant}
      />

      <Card elevation={0} ref={containerRef} style={{ overflow: "hidden" }}>
        <div className={classes.container}>
          {/* LEFT PANEL */}
          <div className={classes.leftPanel} style={{ width: leftWidth }}>
            <div className={classes.searchFieldWrapper}>
              <TextField
                data-testid="search-journal-button"
                type="text"
                value={titleCase(search)}
                name="search"
                onChange={handleSearch}
                fullWidth
                placeholder="Search journal"
              />
            </div>

            {loadingJournals === "loading" && <LoadingGif />}

            {loadingJournals === "success" &&
              (allJournals.length > 0 ? (
                filteredList(allJournals).map((item, idx) => (
                  <ReusableList
                    key={item.id}
                    primaryText={item.head_name}
                    data-testid={"journal-" + idx}
                    id={"journal-" + idx}
                    isActive={item.isActive === "active"}
                    secondaryText={item.head_code}
                    onClick={() => handleClickJournal(item)}
                  />
                ))
              ) : (
                <NoTransaction text="journals" />
              ))}

            {loadingJournals === "error" && (
              <div className="text-center mt-5">
                The server did not return a valid response
              </div>
            )}
          </div>

          {/* DIVIDER */}
          <div className={classes.divider} onMouseDown={startResize} />

          {/* RIGHT PANEL */}
          <div className={classes.rightPanel}>
            <Typography data-testid="journal-title" className={classes.body1}>
              {head_name}
            </Typography>
            <Typography className={classes.body2}>{head_code}</Typography>

            <LedgerEntry
              {...{ actions, isSubmitLedger, setIsSubmitLedger }}
            />

            <JournalList
              isResolved={isResolved}
              item={specificItem}
              actions={actions}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

function mapStateToProps(state) {
  const { snackbars } = state;
  return { snackbars };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Journals);
